import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Pencil, Save, X, Target, Trash2, GitCompare, FileSpreadsheet, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const TOOLTIP_STYLE = { background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12, color: '#0A0A0A' };
const PRODUCTS = ["Fonasa", "Zona", "1727", "Previsional", "Seg. Viaje", "Medis Group", "RXD + 18"];
const YEAR_COLORS = ['#0082D2', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function Analisis() {
  const { canManage, isAuditor } = useAuth();
  const [tab, setTab] = useState('vendedor');
  
  // Vendedor tab state
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendorStats, setVendorStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [editingGoals, setEditingGoals] = useState(false);
  const [editGoals, setEditGoals] = useState({});

  // Comparar tab state
  const [periodoA_mes, setPeriodoA_mes] = useState(new Date().getMonth() + 1);
  const [periodoA_año, setPeriodoA_año] = useState(new Date().getFullYear());
  const [periodoB_mes, setPeriodoB_mes] = useState(new Date().getMonth() + 1);
  const [periodoB_año, setPeriodoB_año] = useState(new Date().getFullYear() - 1);
  const [compareData, setCompareData] = useState({});
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareVendorDetail, setCompareVendorDetail] = useState(null);
  const [compareSelectedVendors, setCompareSelectedVendors] = useState([]);

  // Fonasa tab state
  const [fonasaMes, setFonasaMes] = useState(new Date().getMonth() + 1);
  const [fonasaAño, setFonasaAño] = useState(new Date().getFullYear());
  const [fonasaData, setFonasaData] = useState([]);
  const [fonasaLoading, setFonasaLoading] = useState(false);

  useEffect(() => {
    api.getVendors().then(setVendors).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchVendorData = useCallback(async () => {
    if (!selectedVendor) return;
    setLoading(true);
    try {
      const [stats, salesData] = await Promise.all([
        api.getVendorStats(selectedVendor),
        api.getSales({ vendedor_id: selectedVendor, month: selectedMonth, year: 2026 })
      ]);
      setVendorStats(stats);
      setSales(salesData);
    } catch (error) { /* handled */ } finally { setLoading(false); }
  }, [selectedVendor, selectedMonth]);

  useEffect(() => { fetchVendorData(); }, [fetchVendorData]);

  const fetchCompareData = useCallback(async () => {
    setCompareLoading(true);
    try {
      const results = {};
      const labelA = `${MONTHS[periodoA_mes - 1]} ${periodoA_año}`;
      const labelB = `${MONTHS[periodoB_mes - 1]} ${periodoB_año}`;
      const [dataA, dataB] = await Promise.all([
        api.getVendorsRanking(periodoA_mes, periodoA_año, 'mensual'),
        api.getVendorsRanking(periodoB_mes, periodoB_año, 'mensual')
      ]);
      results[labelA] = dataA;
      results[labelB] = dataB;
      setCompareData(results);
    } catch (e) { /* handled */ } finally { setCompareLoading(false); }
  }, [periodoA_mes, periodoA_año, periodoB_mes, periodoB_año]);

  const compareKeys = Object.keys(compareData);

  const toggleCompareVendor = (vendorId) => {
    setCompareSelectedVendors(prev => 
      prev.includes(vendorId) ? prev.filter(id => id !== vendorId) : [...prev, vendorId]
    );
  };

  useEffect(() => { if (tab === 'comparar') fetchCompareData(); }, [tab, fetchCompareData]);

  // Export functions for "Por Vendedor" tab
  const exportVendorExcel = () => {
    if (!vendorStats) return;
    const data = monthsWithData.map(m => {
      const row = { Mes: m.name };
      PRODUCTS.forEach(p => { row[p] = m[p] || 0; });
      row['Total'] = m.servicios;
      return row;
    });
    if (monthsWithData.length > 1) {
      const totalRow = { Mes: 'TOTAL' };
      PRODUCTS.forEach(p => { totalRow[p] = monthsWithData.reduce((s, m) => s + (m[p] || 0), 0); });
      totalRow['Total'] = monthsWithData.reduce((s, m) => s + m.servicios, 0);
      data.push(totalRow);
    }
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Productos por Mes');
    const vendorName = vendors.find(v => v.id === selectedVendor)?.nombre || 'vendedor';
    XLSX.writeFile(wb, `analisis_${vendorName}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportVendorPDF = () => {
    if (!vendorStats) return;
    const doc = new jsPDF('landscape');
    const vendorName = vendors.find(v => v.id === selectedVendor)?.nombre || 'Vendedor';
    doc.setFontSize(16);
    doc.text(`Analisis - ${vendorName}`, 14, 20);
    doc.setFontSize(9);
    doc.text(`Fecha: ${new Date().toLocaleDateString('es-UY')}`, 14, 27);
    const headers = ['Mes', ...PRODUCTS, 'Total'];
    const body = monthsWithData.map(m => [m.name, ...PRODUCTS.map(p => m[p] || 0), m.servicios]);
    if (monthsWithData.length > 1) {
      body.push(['TOTAL', ...PRODUCTS.map(p => monthsWithData.reduce((s, m) => s + (m[p] || 0), 0)), monthsWithData.reduce((s, m) => s + m.servicios, 0)]);
    }
    autoTable(doc, { head: [headers], body, startY: 32, styles: { fontSize: 8 }, headStyles: { fillColor: [0, 32, 91] } });
    doc.save(`analisis_${vendorName}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Export functions for "Comparar Periodos" tab
  const exportCompareExcel = () => {
    const filteredVendors = buildVendorCompare().filter(v => compareSelectedVendors.includes(v.id));
    if (filteredVendors.length === 0) return;
    const labelA = compareKeys[0] || 'Periodo A';
    const labelB = compareKeys[1] || 'Periodo B';
    const data = filteredVendors.map(v => {
      const row = { Vendedor: v.nombre };
      PRODUCTS.forEach(p => {
        row[`${p} A`] = v.productos?.periodo0?.[p] || 0;
        row[`${p} B`] = v.productos?.periodo1?.[p] || 0;
      });
      row['Total A'] = v.periodo0 || 0;
      row['Total B'] = v.periodo1 || 0;
      row['Diferencia'] = (v.periodo0 || 0) - (v.periodo1 || 0);
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comparar Periodos');
    XLSX.writeFile(wb, `comparar_periodos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportComparePDF = () => {
    const filteredVendors = buildVendorCompare().filter(v => compareSelectedVendors.includes(v.id));
    if (filteredVendors.length === 0) return;
    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.text('Comparar Periodos', 14, 20);
    doc.setFontSize(9);
    const labelA = compareKeys[0] || 'Periodo A';
    const labelB = compareKeys[1] || 'Periodo B';
    doc.text(`${labelA} vs ${labelB} - ${new Date().toLocaleDateString('es-UY')}`, 14, 27);
    const headers = ['Vendedor', ...PRODUCTS.map(p => `${p} A/B`), 'Total A/B', 'Dif.'];
    const body = filteredVendors.map(v => [
      v.nombre,
      ...PRODUCTS.map(p => `${v.productos?.periodo0?.[p] || 0} / ${v.productos?.periodo1?.[p] || 0}`),
      `${v.periodo0 || 0} / ${v.periodo1 || 0}`,
      (v.periodo0 || 0) - (v.periodo1 || 0)
    ]);
    autoTable(doc, { head: [headers], body, startY: 32, styles: { fontSize: 7 }, headStyles: { fillColor: [0, 32, 91] } });
    doc.save(`comparar_periodos_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const fetchFonasaData = useCallback(async () => {
    setFonasaLoading(true);
    try { const data = await api.getFonasaSales(fonasaMes, fonasaAño); setFonasaData(data); } catch (e) { /* handled */ } finally { setFonasaLoading(false); }
  }, [fonasaMes, fonasaAño]);

  useEffect(() => { if (tab === 'fonasa') fetchFonasaData(); }, [tab, fetchFonasaData]);

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('Eliminar esta venta? Esta accion no se puede deshacer.')) return;
    try { await api.deleteSale(saleId); toast.success('Venta eliminada'); fetchVendorData(); } catch (error) { toast.error('Error al eliminar venta'); }
  };

  const startEditGoals = async () => {
    try { const goals = await api.getVendorGoals(selectedVendor); setEditGoals(goals); setEditingGoals(true); } catch (error) { toast.error('Error al cargar metas'); }
  };

  const saveGoals = async () => {
    try { await api.setVendorGoals(selectedVendor, editGoals); toast.success('Metas guardadas'); setEditingGoals(false); fetchVendorData(); } catch (error) { toast.error('Error al guardar'); }
  };

  const monthlyEvolution = vendorStats?.monthly_evolution?.map(m => ({ ...m, name: MONTHS[m.mes - 1] })) || [];
  const monthsWithData = monthlyEvolution.filter(m => m.servicios > 0);

  const buildCompareChart = () => {
    return PRODUCTS.map(product => {
      const row = { producto: product };
      compareKeys.forEach((key, idx) => {
        const rankings = compareData[key] || [];
        row[`periodo${idx}`] = rankings.reduce((sum, v) => sum + (v.por_producto?.[product] || 0), 0);
      });
      return row;
    });
  };

  const buildVendorCompare = () => {
    const vendorMap = {};
    compareKeys.forEach((key, idx) => {
      (compareData[key] || []).forEach(v => {
        if (!vendorMap[v.id]) vendorMap[v.id] = { id: v.id, nombre: v.nombre, productos: {} };
        vendorMap[v.id][`periodo${idx}`] = Object.values(v.por_producto || {}).reduce((s, n) => s + n, 0);
        vendorMap[v.id].productos[`periodo${idx}`] = v.por_producto || {};
      });
    });
    return Object.values(vendorMap).sort((a, b) => (b.periodo0 || 0) - (a.periodo0 || 0));
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="space-y-6" data-testid="analisis">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Analisis</h1>
        <p className="text-[13px] text-gray-500 mt-0.5">Analisis detallado por vendedor y comparacion entre anos</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="analisis-tabs">
        <button onClick={() => setTab('vendedor')}
          className={`px-4 py-2 rounded-md text-[13px] font-medium transition-all ${tab === 'vendedor' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-vendedor">
          Por Vendedor
        </button>
        <button onClick={() => setTab('comparar')}
          className={`px-4 py-2 rounded-md text-[13px] font-medium transition-all flex items-center gap-1.5 ${tab === 'comparar' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-comparar">
          <GitCompare size={14} /> Comparar Periodos
        </button>
        <button onClick={() => setTab('fonasa')}
          className={`px-4 py-2 rounded-md text-[13px] font-medium transition-all flex items-center gap-1.5 ${tab === 'fonasa' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-fonasa">
          <FileSpreadsheet size={14} /> Fonasa
        </button>
      </div>

      {/* TAB: Por Vendedor */}
      {tab === 'vendedor' && (
        <>
          <div className="mac-card p-5">
            <label className="form-label">Seleccionar Vendedor</label>
            <select value={selectedVendor} onChange={(e) => { setSelectedVendor(e.target.value); setEditingGoals(false); }} className="form-input max-w-md" data-testid="vendor-selector">
              <option value="">Seleccionar vendedor</option>
              {vendors.map(v => (<option key={v.id} value={v.id}>{v.nombre}</option>))}
            </select>
          </div>

          {loading && selectedVendor && <div className="flex items-center justify-center h-32"><div className="text-gray-500 text-[13px]">Cargando...</div></div>}

          {vendorStats && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
                  <div className="metric-card"><div className="metric-label">Total Ventas {currentYear}</div><div className="metric-value">{vendorStats.services_2026}</div></div>
                  <div className="metric-card"><div className="metric-label">Socios {currentYear}</div><div className="metric-value">{vendorStats.socios_2026}</div></div>
                  <div className="metric-card"><div className="metric-label">Este Mes</div><div className="metric-value">{vendorStats.services_month}</div></div>
                  <div className="metric-card"><div className="metric-label">Hoy</div><div className="metric-value">{vendorStats.services_today}</div></div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={exportVendorExcel} className="btn-secondary flex items-center gap-2 text-[12px]" data-testid="vendor-export-excel"><FileSpreadsheet size={14} /> Excel</button>
                <button onClick={exportVendorPDF} className="btn-secondary flex items-center gap-2 text-[12px]" data-testid="vendor-export-pdf"><FileText size={14} /> PDF</button>
              </div>

              <div className="mac-card overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-200">
                  <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Productos por Mes {currentYear}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-header">Mes</th>
                        {PRODUCTS.map(p => <th key={p} className="table-header text-center text-[10px]">{p}</th>)}
                        <th className="table-header text-center">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthsWithData.map(m => (
                        <tr key={m.name} className="table-row">
                          <td className="table-cell font-medium">{m.name}</td>
                          {PRODUCTS.map(p => (
                            <td key={p} className="table-cell text-center text-[13px]">{m[p] || 0}</td>
                          ))}
                          <td className="table-cell text-center font-semibold text-[#00205B]">{m.servicios}</td>
                        </tr>
                      ))}
                      {monthsWithData.length > 1 && (
                        <tr className="border-t-2 border-gray-300">
                          <td className="table-cell font-bold text-[#00205B]">TOTAL</td>
                          {PRODUCTS.map(p => (
                            <td key={p} className="table-cell text-center font-bold text-[#00205B]">{monthsWithData.reduce((s, m) => s + (m[p] || 0), 0)}</td>
                          ))}
                          <td className="table-cell text-center font-bold text-[#00205B]">{monthsWithData.reduce((s, m) => s + m.servicios, 0)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mac-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2"><Target size={14} /> Metas del Vendedor</h3>
                  {canManage && !editingGoals && <button onClick={startEditGoals} className="btn-secondary flex items-center gap-1.5 text-[12px]" data-testid="edit-goals-btn"><Pencil size={13} /> Editar Metas</button>}
                  {editingGoals && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditingGoals(false)} className="btn-secondary flex items-center gap-1.5 text-[12px]"><X size={13} /> Cancelar</button>
                      <button onClick={saveGoals} className="btn-primary flex items-center gap-1.5 text-[12px]" data-testid="save-goals-btn"><Save size={13} /> Guardar</button>
                    </div>
                  )}
                </div>
                {editingGoals ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {Object.entries(editGoals).map(([service, goal]) => (
                      <div key={service} className="bg-[#F3F4F6] rounded-md p-3">
                        <label className="text-[12px] font-medium text-gray-700 block mb-1.5 truncate">{service}</label>
                        <input type="number" min="0" value={goal} onChange={(e) => setEditGoals(prev => ({ ...prev, [service]: parseInt(e.target.value) || 0 }))} className="form-input" data-testid={`goal-${service.toLowerCase().replace(/\s/g, '-')}`} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {vendorStats.individual_goals && Object.entries(vendorStats.individual_goals).map(([service, data]) => (
                      <div key={service} className="bg-[#F3F4F6] rounded-md p-3">
                        <div className="flex justify-between items-center mb-2"><span className="text-[12px] font-medium text-gray-700 truncate">{service}</span><span className="text-[11px] text-gray-500">{data.current}/{data.goal}</span></div>
                        <div className="progress-bar"><div className="progress-fill" style={{ width: `${data.percentage}%`, backgroundColor: data.percentage >= 100 ? '#10B981' : data.percentage >= 50 ? '#0082D2' : '#F59E0B' }} /></div>
                        <div className="text-[10px] text-gray-500 mt-1">{data.percentage}%</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mac-card overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Historial de Ventas</h3>
                  <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="form-input w-auto text-[12px]" data-testid="month-selector">
                    {MONTHS.map((m, i) => (<option key={`month-${i}`} value={i + 1}>{m}</option>))}
                  </select>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr><th className="table-header">Fecha</th><th className="table-header">Cedula</th><th className="table-header">Socio</th><th className="table-header">Productos</th><th className="table-header">Mutualista Ant.</th>{isAuditor && <th className="table-header">Acciones</th>}</tr></thead>
                    <tbody>
                      {sales.length === 0 ? (
                        <tr><td colSpan={isAuditor ? 6 : 5} className="table-cell text-center text-gray-500">Sin ventas este mes</td></tr>
                      ) : (sales.map(sale => (
                        <tr key={sale.id} className="table-row">
                          <td className="table-cell text-[12px]">{new Date(sale.fecha).toLocaleDateString('es-UY')}</td>
                          <td className="table-cell text-[12px] text-gray-600">{sale.numero_documento}</td>
                          <td className="table-cell font-medium text-[#0A0A0A]">{sale.nombre_socio} {sale.apellido_socio}</td>
                          <td className="table-cell"><div className="flex flex-wrap gap-1">{sale.servicios.map((s, j) => (<span key={`${sale.id}-svc-${s}-${j}`} className="inline-flex px-2 py-0.5 rounded-full bg-[#0082D2]/10 text-[#0082D2] text-[10px] font-medium">{s}</span>))}</div></td>
                          <td className="table-cell text-[12px] text-gray-500">{sale.mutualista_anterior}</td>
                          {isAuditor && <td className="table-cell"><button onClick={() => handleDeleteSale(sale.id)} className="btn-ghost p-1.5 text-[#EF4444]" data-testid={`delete-sale-${sale.id}`}><Trash2 size={14} /></button></td>}
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* TAB: Comparar Periodos */}
      {tab === 'comparar' && (
        <>
          <div className="mac-card p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: YEAR_COLORS[0] }}></span>
                  <span className="text-[13px] font-semibold text-[#0A0A0A]">Periodo A</span>
                </div>
                <div className="flex items-center gap-3">
                  <select value={periodoA_mes} onChange={e => setPeriodoA_mes(parseInt(e.target.value))} className="form-input text-[13px] py-2" style={{width: '140px'}} data-testid="periodo-a-mes">
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={periodoA_año} onChange={e => setPeriodoA_año(parseInt(e.target.value))} className="form-input text-[13px] py-2" style={{width: '90px'}} data-testid="periodo-a-año">
                    {Array.from({ length: 5 }, (_, i) => currentYear - i).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: YEAR_COLORS[1] }}></span>
                  <span className="text-[13px] font-semibold text-[#0A0A0A]">Periodo B</span>
                </div>
                <div className="flex items-center gap-3">
                  <select value={periodoB_mes} onChange={e => setPeriodoB_mes(parseInt(e.target.value))} className="form-input text-[13px] py-2" style={{width: '140px'}} data-testid="periodo-b-mes">
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={periodoB_año} onChange={e => setPeriodoB_año(parseInt(e.target.value))} className="form-input text-[13px] py-2" style={{width: '90px'}} data-testid="periodo-b-año">
                    {Array.from({ length: 5 }, (_, i) => currentYear - i).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {compareLoading ? (
            <div className="flex items-center justify-center h-32"><div className="text-gray-500 text-[13px]">Cargando...</div></div>
          ) : (
            <>
              {/* Selector de vendedores */}
              <div className="mac-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Seleccionar vendedores:</label>
                  <div className="flex gap-2">
                    <button onClick={() => setCompareSelectedVendors(vendors.map(v => v.id))} className="text-[11px] text-[#0082D2] hover:underline" data-testid="select-all-vendors">Todos</button>
                    <button onClick={() => setCompareSelectedVendors([])} className="text-[11px] text-gray-500 hover:underline" data-testid="deselect-all-vendors">Ninguno</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {vendors.map(v => (
                    <button key={v.id} onClick={() => toggleCompareVendor(v.id)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
                        compareSelectedVendors.includes(v.id) 
                          ? 'bg-[#00205B] text-white border-[#00205B]' 
                          : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                      }`}
                      data-testid={`vendor-chip-${v.id}`}
                    >
                      {v.nombre}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tabla: Vendedores con desglose por producto */}
              <div className="mac-card overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Desglose por Producto</h3>
                  <div className="flex gap-2">
                    <button onClick={exportCompareExcel} className="btn-secondary flex items-center gap-2 text-[11px] py-1" data-testid="compare-export-excel"><FileSpreadsheet size={13} /> Excel</button>
                    <button onClick={exportComparePDF} className="btn-secondary flex items-center gap-2 text-[11px] py-1" data-testid="compare-export-pdf"><FileText size={13} /> PDF</button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  {compareSelectedVendors.length === 0 ? (
                    <div className="p-5 text-center text-gray-500 text-[13px]">Selecciona al menos un vendedor</div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr>
                          <th className="table-header">Vendedor</th>
                          {PRODUCTS.map(p => (
                            <th key={p} className="table-header text-center text-[10px]">{p}</th>
                          ))}
                          <th className="table-header text-center">Total</th>
                          <th className="table-header text-center">Dif.</th>
                        </tr>
                        <tr>
                          <th className="table-header"></th>
                          {PRODUCTS.map(p => (
                            <th key={p} className="table-header text-center text-[9px] text-gray-400">A / B</th>
                          ))}
                          <th className="table-header text-center text-[9px] text-gray-400">A / B</th>
                          <th className="table-header"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildVendorCompare().filter(v => compareSelectedVendors.includes(v.id)).map(vendor => {
                          const totalA = vendor.periodo0 || 0;
                          const totalB = vendor.periodo1 || 0;
                          const diff = totalA - totalB;
                          return (
                            <tr key={vendor.nombre} className="table-row">
                              <td className="table-cell font-medium text-[#0A0A0A] text-[12px]">{vendor.nombre}</td>
                              {PRODUCTS.map(p => {
                                const a = vendor.productos?.periodo0?.[p] || 0;
                                const b = vendor.productos?.periodo1?.[p] || 0;
                                return (
                                  <td key={p} className="table-cell text-center text-[12px]">
                                    {a} / {b}
                                  </td>
                                );
                              })}
                              <td className="table-cell text-center font-semibold text-[12px] text-[#00205B]">
                                {totalA} / {totalB}
                              </td>
                              <td className="table-cell text-center">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${diff > 0 ? 'bg-[#10B981]/10 text-[#10B981]' : diff < 0 ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-gray-100 text-gray-500'}`}>
                                  {diff > 0 ? `+${diff}` : diff}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}

        </>
      )}
      {/* TAB: Fonasa */}
      {tab === 'fonasa' && (
        <>
          <div className="mac-card p-5">
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <label className="form-label">Mes</label>
                <select value={fonasaMes} onChange={(e) => setFonasaMes(Number(e.target.value))} className="form-input w-40" data-testid="fonasa-month">
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Ano</label>
                <select value={fonasaAño} onChange={(e) => setFonasaAño(Number(e.target.value))} className="form-input w-28" data-testid="fonasa-year">
                  {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="ml-auto text-right">
                <div className="text-2xl font-bold text-[#0082D2]">{fonasaData.length}</div>
                <div className="text-[11px] text-gray-500">Socios Fonasa</div>
              </div>
            </div>
          </div>

          <div className="mac-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-header">#</th>
                    <th className="table-header">Cedula</th>
                    <th className="table-header">Socio</th>
                    <th className="table-header">Edad</th>
                    <th className="table-header">Sexo</th>
                    <th className="table-header">Mutualista Ant.</th>
                    <th className="table-header">Vendedor</th>
                    <th className="table-header">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {fonasaLoading ? (
                    <tr><td colSpan="8" className="table-cell text-center text-gray-500">Cargando...</td></tr>
                  ) : fonasaData.length === 0 ? (
                    <tr><td colSpan="8" className="table-cell text-center text-gray-500">Sin ventas Fonasa en este periodo</td></tr>
                  ) : (
                    fonasaData.map((s, idx) => (
                      <tr key={s.id || idx} className="table-row">
                        <td className="table-cell text-gray-400 text-[12px]">{idx + 1}</td>
                        <td className="table-cell font-medium text-[#0A0A0A]">{s.numero_documento}</td>
                        <td className="table-cell">{s.nombre_socio} {s.apellido_socio}</td>
                        <td className="table-cell text-center">{s.edad || '-'}</td>
                        <td className="table-cell text-center">{s.sexo || '-'}</td>
                        <td className="table-cell text-gray-600">{s.mutualista_anterior}</td>
                        <td className="table-cell text-[12px] text-gray-500">{s.vendedor_nombre}</td>
                        <td className="table-cell text-[12px] text-gray-500">{s.fecha ? new Date(s.fecha + 'T12:00:00').toLocaleDateString('es-UY') : ''}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
